<script lang="ts" setup>
import { computed, toBlog, useLocalePath, useNuxtApp, useRoute, watch, watchEffect } from '#imports'
import { AvatarSize } from '~/layouts/drive/avatar.script'
import Avatar from '~/layouts/drive/Avatar.vue'
import { useUserInfo } from '~/store/userInfo'
import { RoutePath, RoutePathOfDrive, SupportLocale } from '~/types/enum'
import MobileMenuItem from './MobileMenuItem.vue'
import topBarLinkClick from './topBarLinkClick'

const userInfo = useUserInfo()
const localePath = useLocalePath()

interface Props {
  visible: boolean
}

const props = defineProps<Props>()

interface EmitsInterface {
  (e: 'close'): void
}

const emits = defineEmits<EmitsInterface>()

const route = useRoute()
watch(route, () => {
  emits('close')
})

interface CollapseItem {
  title: string
  name: string
  link?: RoutePath | RoutePathOfDrive | string
  target?: '_blank'
  icon?: 'icon-mobile' | 'icon-pc' | 'icon-web' | 'icon-tv'
  children?: CollapseItem[]
  onClick?: VoidFunction | ((val: string) => Promise<void>)
}

const { t, locale, setLocale, locales } = useNuxtApp().$i18n
const availableLocales = computed(() =>
  (locales.value as { code: SupportLocale; name: string }[]).filter(i => i.code !== locale.value),
)
const localeName = computed(() => (locales.value as Locales).find(el => el.code === locale.value)!.name)

const collapseItems = computed<CollapseItem[]>(() => [
  {
    title: t('basic-home-page'),
    name: 'PikPak-introduce',
    link: RoutePath.HOME,
  },
  {
    title: t('basic-platform'),
    name: 'platform',
    children: [
      {
        title: t('basic-mobile-app'),
        name: 'PikPak-mobile',
        icon: 'icon-mobile',
        link: RoutePath.MOBILE_APP,
      },
      {
        title: t('basic-desktop-app'),
        name: 'PikPak-pc',
        icon: 'icon-pc',
        link: RoutePath.DESKTOP_APP,
      },
      {
        title: t('tv-client'),
        name: 'PikPak-pc',
        icon: 'icon-tv',
        link: RoutePath.TV_APP,
      },
      {
        title: t('basic-web-app'),
        name: 'PikPak-web',
        icon: 'icon-web',
        link: RoutePathOfDrive.ALL,
        target: '_blank',
      },
    ],
  },
  {
    title: t('basic-price'),
    name: 'price',
    link: RoutePathOfDrive.PAYMENT,
    target: '_blank',
  },
  {
    title: t('akeb2cKjhVg3fG9rvBhY'),
    name: 'referral',
    link: RoutePathOfDrive.REFERRAL,
    target: '_blank',
  },
  {
    title: t('resources'),
    name: 'support',
    children: [
      {
        title: t('help-center'),
        name: 'help-center',
        link: RoutePath.HELP_CENTER,
      },
      {
        title: 'FAQ',
        name: 'FAQ',
        link: RoutePath.FAQ,
      },
      {
        title: t('zpkOoV1ZakY1xxk4B06Ze'),
        name: 'blog',
        onClick: () => {
          window.location.href = toBlog()
        },
      },
      {
        title: t('basic-contact-us'),
        name: 'contact-us',
        link: RoutePath.CONTACT_US,
      },
      {
        title: t('transparency-report'),
        name: 'transparency-report',
        link: RoutePath.TRANSPARENCY_REPORT,
      },
    ],
  },
  {
    title: localeName.value,
    name: 'language',
    children: availableLocales.value.map(el => ({
      name: el.code,
      title: el.name,
      onClick: async val => {
        await setLocale(val as SupportLocale)
        emits('close')
      },
    })),
  },
])

const handleItemClick = () => {
  emits('close')
}

const dialogVisible = computed(() => props.visible)
// 解决打开menu页面滚动的问题
watchEffect(() => {
  if (import.meta.client) {
    document.body.style.overflowY = dialogVisible.value ? 'hidden' : 'unset'
  }
})
</script>

<template>
  <div
    v-if="dialogVisible"
    class="pikpak-mobile-menu"
  >
    <ElCollapse>
      <template
        v-for="item in collapseItems"
        :key="item.name"
      >
        <MobileMenuItem
          v-if="item.link"
          @click="handleItemClick"
        >
          <NuxtLink
            :target="item.target"
            :to="item.target ? item.link : localePath(item.link)"
            class="item-link"
            @click="topBarLinkClick(item.link)"
          >
            {{ item.title }}
          </NuxtLink>
        </MobileMenuItem>
        <ElCollapseItem
          v-else
          :name="item.name"
          :title="item.title"
        >
          <template
            v-for="subItem in item.children"
            :key="subItem.name"
          >
            <MobileMenuItem
              v-if="subItem.onClick"
              @click="handleItemClick"
            >
              <div
                class="item-link"
                @click="subItem.onClick?.(subItem.name)"
              >
                {{ subItem.title }}
              </div>
            </MobileMenuItem>
            <MobileMenuItem
              v-else-if="subItem.link"
              @click="handleItemClick"
            >
              <NuxtLink
                :target="subItem.target"
                :to="subItem.target ? subItem.link : localePath(subItem.link)"
                class="item-link"
                @click="topBarLinkClick(subItem.link)"
              >
                <i
                  v-if="subItem.icon"
                  :class="subItem.icon"
                  class="icon"
                />
                <span>
                  {{ subItem.title }}
                </span>
              </NuxtLink>
            </MobileMenuItem>
          </template>
        </ElCollapseItem>
      </template>
      <MobileMenuItem @close="dialogVisible = false">
        <a
          v-if="userInfo.userId"
          :href="RoutePathOfDrive.ALL"
          class="user-info pp-link-button"
          target="_blank"
        >
          <Avatar
            :premium="userInfo.premium"
            :size="AvatarSize.SMALL"
            :src="userInfo.avatar"
          />
          <span class="user-name">{{ userInfo.username }}</span>
        </a>
        <a
          v-else
          :href="RoutePathOfDrive.LOGIN"
          class="pp-link-button"
        >
          {{ $t('basic-sign-up-or-in') }}
        </a>
      </MobileMenuItem>
    </ElCollapse>
  </div>
</template>

<style lang="less" scoped>
.pikpak-mobile-menu {
  position: fixed;
  top: 60px;
  width: 100vw;
  height: var(--vh, 100vh);
  overflow-y: scroll;
  background-color: #fff;
  padding: 0 24px;

  .mobile-menu-item {
    border-bottom: 1px solid #ebeef5;

    &:nth-last-child(1) {
      border-bottom: none;
    }
  }

  .item-link {
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    font-size: 14px;
    text-decoration: none;
    color: #000;

    .icon {
      width: 30px;
      height: 30px;
      margin-right: 8px;
      border-radius: 50%;
    }
  }

  .pp-link-button {
    font-size: 14px;
  }

  .el-collapse-item .item-link {
    padding-left: 32px;
  }

  .el-collapse-item:active {
    background-color: transparent;
  }

  .icon-pc {
    background: url('assets/images/basic-layout/desktop-app.svg') no-repeat center/cover;
  }

  .icon-mobile {
    background: url('assets/images/basic-layout/mobile-app.svg') no-repeat center/cover;
  }

  .icon-tv {
    background: url('assets/images/basic-layout/tv-app.svg') no-repeat center/cover;
  }

  .user-info {
    display: flex;
    align-items: center;

    .user-name {
      margin-left: 8px;
      font-weight: 700;
    }
  }

  .icon-web {
    background: url('assets/images/basic-layout/web.svg') no-repeat center/cover;
  }
}
</style>
<style lang="less">
.pikpak-mobile-menu {
  .el-collapse-item__content {
    padding-bottom: 0;
  }
}
</style>
